import { CloudAsset, CloudAssetSource } from '#types/types';
import { isInternalAssetUrl } from '#lib/azure-shared';

const sanitizeUrl = (candidate: unknown): string | null => {
  if (typeof candidate !== 'string') return null;
  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isTrackableCloudUrl = (url: string | null | undefined): url is string => {
  const sanitized = sanitizeUrl(url);
  if (!sanitized || sanitized.startsWith('data:')) return false;
  if (isInternalAssetUrl(sanitized)) return true;
  return /^https?:\/\//i.test(sanitized);
};

type CloudAssetInput = Partial<CloudAsset> & {
  url?: string | null;
  source?: string | null;
  createdAt?: string | number | Date | null;
  lastSeenAt?: string | number | Date | null;
  timestamp?: string | number | Date | null;
};

const VALID_SOURCES: CloudAssetSource[] = ['generation', 'upload', 'mask', 'version_view', 'diff_view'];
const VALID_SOURCE_SET = new Set<CloudAssetSource>(VALID_SOURCES);

const isValidSource = (value: unknown): value is CloudAssetSource =>
  typeof value === 'string' && VALID_SOURCE_SET.has(value as CloudAssetSource);

const isIsoDateString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && !Number.isNaN(Date.parse(value));

const toIsoString = (value: unknown, fallbackIso: string): string => {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value).toISOString();
  if (isIsoDateString(value)) return value;
  return fallbackIso;
};

const pickFallbackCreatedAt = (input: CloudAssetInput, fallbackIso?: string): string => {
  if (fallbackIso && isIsoDateString(fallbackIso)) {
    return fallbackIso;
  }
  if (input.timestamp instanceof Date) return input.timestamp.toISOString();
  if (typeof input.timestamp === 'number' && Number.isFinite(input.timestamp)) {
    return new Date(input.timestamp).toISOString();
  }
  if (typeof input.timestamp === 'string' && isIsoDateString(input.timestamp)) {
    return input.timestamp;
  }
  return new Date().toISOString();
};

const normalizeCloudAsset = (
  input: CloudAssetInput | null | undefined,
  fallbackSource: CloudAssetSource = 'generation',
  fallbackCreatedAt?: string
): CloudAsset | null => {
  if (!input) return null;

  const url = sanitizeUrl(input.url);
  if (!url) return null;

  const baseCreatedAt = pickFallbackCreatedAt(input, fallbackCreatedAt);
  const createdAt = toIsoString(input.createdAt, baseCreatedAt);
  const lastSeenAt = toIsoString(input.lastSeenAt, createdAt);
  const source = isValidSource(input.source) ? input.source : fallbackSource;

  return { url, source, createdAt, lastSeenAt };
};

const mergeCloudAssets = (
  registry: CloudAsset[],
  incoming: Iterable<CloudAsset>
): CloudAsset[] => {
  const registryMap = new Map<string, CloudAsset>();
  for (const asset of registry) {
    registryMap.set(asset.url, asset);
  }

  for (const asset of incoming) {
    const existing = registryMap.get(asset.url);
    if (!existing) {
      registryMap.set(asset.url, asset);
      continue;
    }

    const mergedCreatedAt =
      new Date(asset.createdAt) < new Date(existing.createdAt) ? asset.createdAt : existing.createdAt;
    const mergedLastSeenAt =
      new Date(asset.lastSeenAt) > new Date(existing.lastSeenAt) ? asset.lastSeenAt : existing.lastSeenAt;

    registryMap.set(asset.url, {
      url: asset.url,
      source: existing.source,
      createdAt: mergedCreatedAt,
      lastSeenAt: mergedLastSeenAt,
    });
  }

  return Array.from(registryMap.values());
};

export const upsertCloudAssets = (
  registry: CloudAsset[],
  assets: Iterable<CloudAsset>
): CloudAsset[] => mergeCloudAssets(registry, assets);

export const buildCloudAssetEntry = (
  url: string | null | undefined,
  source: CloudAssetSource,
  timestampIso?: string
): CloudAsset | null => {
  const normalized = normalizeCloudAsset(
    {
      url: url || undefined,
      source,
      createdAt: timestampIso,
      lastSeenAt: timestampIso,
    },
    source,
    timestampIso
  );

  if (!normalized || !isTrackableCloudUrl(normalized.url)) {
    return null;
  }

  return normalized;
};
